import { useDiagnoses as useDiagnosesAll, type Diagnosis } from './diagnoses';
import { useSessionTypes as useSessionTypesAll, type SessionType } from './sessionTypes';
import { useLocationTypes as useLocationTypesAll, type LocationType } from './locationTypes';
import { useServiceTypes as useServiceTypesAll, type ServiceType } from './serviceTypes';
import { usePayers as usePayersAll, type Payer } from './payers';
import { useCancelReasons as useCancelReasonsAll, type CancelReason } from './cancelReasons';

/*
 * Picker hooks over the practice-configurable reference-data tables.
 *
 * The full CRUD surface (with Inactive rows) lives in the resource-specific
 * files: ./diagnoses, ./sessionTypes, ./locationTypes, ./serviceTypes,
 * ./payers, ./cancelReasons. Those hooks are the source of truth and the
 * one place the data is fetched. The `useActiveX` hooks below filter the
 * cached list down to Active rows so pickers don't surface deactivated
 * entries — same query key, same fetch, just a filtered view.
 *
 * Re-exporting the canonical types keeps a single source of truth — call
 * sites importing `Payer` from here get the dedicated file's full shape,
 * not a narrower duplicate.
 *
 * Everything here is authenticated-only — not PHI.
 */

export type { Diagnosis, SessionType, LocationType, ServiceType, Payer, CancelReason };

/**
 * Active diagnoses only — Inactive entries are hidden from pickers but
 * remain resolvable on historical records.
 */
export function useActiveDiagnoses() {
    const q = useDiagnosesAll();
    return { ...q, data: q.data?.filter((r) => r.status === 'Active') };
}

/**
 * Active service types, used by pickers that capture a CPT/service code
 * (authorization entry, eligibility-check notes). Inactive entries are
 * hidden from new selections but remain resolvable on historical records.
 */
export function useActiveServiceTypes() {
    const q = useServiceTypesAll();
    return {
        ...q,
        data: q.data
            ?.filter((r) => r.status === 'Active')
            .sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    };
}

/**
 * Active session types — the calendar-level tags ("DTT", "NET", "Supervision")
 * that drive the scheduling modal's service picker. Separate from
 * service_types (the billing catalog); each session type points at a
 * service_type for billing, but the dropdown in scheduling is the session-type
 * list.
 */
export function useActiveSessionTypes() {
    const q = useSessionTypesAll();
    return {
        ...q,
        data: q.data
            ?.filter((r) => r.status === 'Active')
            .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    };
}

/** Active location types — session location picker ("Home", "Clinic", …). */
export function useActiveLocationTypes() {
    const q = useLocationTypesAll();
    return {
        ...q,
        data: q.data
            ?.filter((r) => r.status === 'Active')
            .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    };
}

/** Active cancel reasons — used by the cancellation dialog. */
export function useActiveCancelReasons() {
    const q = useCancelReasonsAll();
    return {
        ...q,
        data: q.data
            ?.filter((r) => r.active)
            .sort((a, b) => a.text.localeCompare(b.text)),
    };
}

/**
 * Active payers only — used by any picker that needs a payer name
 * (eligibility capture, authorization entry). Inactive payers remain
 * resolvable on historical records but don't show up in new entries.
 */
export function useActivePayers() {
    const q = usePayersAll();
    return { ...q, data: q.data?.filter((r) => r.status === 'Active') };
}
